/* Tracking events for sign up flow. */
import {action} from 'mobx';

import store from './store';
import {isBuildABowl, isOneTimeTrial} from './utils';

import axios from '@customAxios';

import {
  sendGoogleTagManagerEvent,
  trackAdWordsConversion,
  trackBingEvent,
  trackCAConversion,
  trackFriendBuyEvent,
  trackGoogleAnalyticsEvent,
  trackPodsightsEvent,
  trackTikTokEvent,
} from '@trackers';

import {DAYS_OF_FOOD_PER_INTERVAL_MAP, DELIVERY_INTERVALS, PET_TYPE} from '@variables';

const _PROFILE_VERSION_TO_CONTENT_TYPE = {
  juicy: 'subscription_personalized',
  bab: 'subscription_flex',
  otto: 'one_time',
};

const _TEN_SECONDS_IN_MILLESECONDS = 10000;

/* Normalizes cost to a weekly cost. */
const getCostPerWeek = (costBeforeTaxAndPromo, daysOfFood) => {
  // Build a bowl cost per week is calculated using daysOfFood derived from delivery interval
  if (isBuildABowl()) {
    const deliveryInterval = store.get('deliveryInterval') || DELIVERY_INTERVALS.BIWEEKLY;
    daysOfFood = DAYS_OF_FOOD_PER_INTERVAL_MAP[deliveryInterval];
  }
  return parseFloat((costBeforeTaxAndPromo / (daysOfFood / 7)).toFixed(2));
};

const buildFacebookProps = (
  petsRecipesAndMotivations,
  firstPet,
  costBeforeTaxAndPromo,
  daysOfFood
) => {
  const props = {};
  if (petsRecipesAndMotivations.numPets) {
    // Count number of dogs, cats and recipes to send to FB.
    props.num_pets = petsRecipesAndMotivations.numPets;
    props.num_cats = petsRecipesAndMotivations.numCats;
    props.num_dogs = petsRecipesAndMotivations.numDogs;
    props.content_ids = petsRecipesAndMotivations.recipes.concat(
      petsRecipesAndMotivations.purchasingMotivations
    );
    // Determine if the first pet is small, medium or large.
    if (firstPet.weight < 20) {
      props.pet_size = 'small';
    } else if (firstPet.weight < 40) {
      props.pet_size = 'medium';
    } else {
      props.pet_size = 'large';
    }
    // Determine if the first pet is at ideal, over or under weight.
    if (firstPet.weight) {
      if (firstPet.weight === firstPet.currentWeight) {
        props.weight_status = 'ideal';
      } else if (firstPet.weight > firstPet.currentWeight) {
        props.weight_status = 'under';
      } else if (firstPet.weight < firstPet.currentWeight) {
        props.weight_status = 'over';
      }
    } else {
      props.weight_status = 'ideal';
    }
    // Determine if first pet is a puppy.
    props.is_puppy = firstPet.petType === PET_TYPE.DOG && firstPet.isPuppy;
  }

  if (costBeforeTaxAndPromo) {
    props.value = isOneTimeTrial()
      ? costBeforeTaxAndPromo
      : getCostPerWeek(costBeforeTaxAndPromo, daysOfFood);
    props.currency = 'USD';
  }
  props.content_type = _PROFILE_VERSION_TO_CONTENT_TYPE[store.get('version')];

  return props;
};

const friendBuyReferralCode = () => {
  const urlParams = new URLSearchParams(window.location.search);
  return urlParams.get('referralCode') || '';
};

const buildTikTokProps = (petsRecipesAndMotivations, costBeforeTaxAndPromo, daysOfFood) => {
  if (petsRecipesAndMotivations && petsRecipesAndMotivations.numPets) {
    const props = {};
    props.contents = petsRecipesAndMotivations.recipes.map((recipe) => {
      return {content_id: recipe, quantity: 1};
    });

    if (costBeforeTaxAndPromo) {
      props.value = isOneTimeTrial()
        ? costBeforeTaxAndPromo
        : getCostPerWeek(costBeforeTaxAndPromo, daysOfFood);
      props.currency = 'USD';
    }

    return props;
  }
};

const createAndStoreFacebookProps = () => {
  const pets = store.get('pets');
  const props = pets[0]
    ? buildFacebookProps(
        getNumberOfPetsRecipesAndMotivations(),
        pets[0],
        store.get('orderCosts').subscriptionPlanSubtotal || store.get('orderCosts').subtotal,
        store.get('daysOfFood')
      )
    : {};

  // Format and save to send in signup payload for server side Facebook event tracking.
  formatAndStoreServerPropsForFacebook({...props});

  return props;
};

const getNumberOfPetsRecipesAndMotivations = () => {
  const pets = store.get('pets');
  if (pets.length) {
    // Count number of dogs, cats and recipes to send to FB.
    let numDogs = 0;
    let numCats = 0;
    const recipes = new Set();
    const purchasingMotivations = new Set();

    pets.forEach((pet) => {
      if (pet.petType === 'CAT') {
        numCats++;
      } else {
        numDogs++;
      }
      if (pet.recipeMix.length) {
        pet.recipeMix.forEach((recipe) => {
          if (recipe) {
            recipes.add(recipe);
          }
        });
      }
      if (pet.recipe) {
        recipes.add(pet.recipe);
      }
    });

    store.get('petSurveys').forEach((petSurvey) => {
      if (petSurvey.purchasingMotivations) {
        petSurvey.purchasingMotivations.forEach((purchasingMotivation) =>
          purchasingMotivations.add(purchasingMotivation)
        );
      }
    });

    return {
      numPets: pets.length,
      numCats: numCats,
      numDogs: numDogs,
      recipes: [...recipes],
      purchasingMotivations: [...purchasingMotivations],
    };
  }
};

const getPaidTotal = () => {
  const totalPaid = store.get('orderCosts').costAfterPromo + store.get('orderCosts').salesTax;
  return parseFloat(totalPaid.toFixed(2));
};

/* Utility function to fire facebook pixel events with custom parameters set. */
const fireFacebookPixel = (eventName) => {
  const props = createAndStoreFacebookProps();

  // eventID and eventName used for deduplicating server and client events.
  const eventID = {
    eventID: store.get('uuid'),
  };

  // Only track event if profile has dogs.
  if (props.num_dogs) {
    window.fbq && window.fbq('track', eventName, props, eventID);
  }
};

const formatAndStoreServerPropsForFacebook = action((props) => {
  // Format props for Subscribe event.
  props.custom_properties = {};
  const customPropsKeys = [
    'num_pets',
    'num_dogs',
    'num_cats',
    'pet_size',
    'weight_status',
    'is_puppy',
  ];
  customPropsKeys.map((key) => {
    if (key in props) {
      props.custom_properties[key] = props[key];
      delete props[key];
    }
  });
  store.set('facebookSubscribeProps', props);
});

/* Utility function to fire tiktok pixel events with parameters set. */
const fireTikTokPixel = (eventName) => {
  const props = buildTikTokProps(
    getNumberOfPetsRecipesAndMotivations(),
    store.get('orderCosts').subscriptionPlanSubtotal || store.get('orderCosts').subtotal,
    store.get('daysOfFood')
  );
  trackTikTokEvent(eventName, props);
};

/* Function to track facebook CAPI events on the server to deduplicate events. */
const trackServerSideFacebookEvent = function (eventName) {
  // If no profile has been saved yet, delay sending event.
  if (!store.get('profileKeyUrlsafe')) {
    setTimeout(() => trackServerSideFacebookEvent(eventName), _TEN_SECONDS_IN_MILLESECONDS);
    return;
  }
  createAndStoreFacebookProps();
  const facebookCustomProps = store.get('facebookSubscribeProps');

  const data = {
    ccpaOptOut: window.IS_CCPA_OPTED_OUT,
    eventName: eventName,
    facebookCustomProps: facebookCustomProps,
    uuid: store.get('uuid'),
  };

  return axios('/api/facebook/send_event', {
    method: 'POST',
    data: data,
  }).catch(() => {});
};

const trackCreateMealPlan = function () {
  sendGoogleTagManagerEvent({
    event: 'CreateMealPlan',
    uuid: store.get('uuid'),
  });
  sendGoogleTagManagerEvent({
    event: 'step_1_create_meal_plan',
    uuid: store.get('uuid'),
  });
};

const trackAddPurchasingMotivations = () => {
  fireFacebookPixel('ViewContent');
  trackServerSideFacebookEvent('ViewContent');
  sendGoogleTagManagerEvent({
    event: 'PurchasingMotivationsAdded',
    uuid: store.get('uuid'),
  });
  sendGoogleTagManagerEvent({
    event: 'step_2_purchasing_motivations_added',
    uuid: store.get('uuid'),
  });
};

const trackStartBuildABowl = function () {
  fireFacebookPixel('AddToCart');
  trackServerSideFacebookEvent('AddToCart');
  sendGoogleTagManagerEvent({
    event: 'StartBuildABowl',
    uuid: store.get('uuid'),
  });
};

const trackProfileStart = function () {
  trackAdWordsConversion('AW-936326366/oKtTCIu6vnMQ3um8vgM');
  trackBingEvent('profile started');
  fireFacebookPixel('AddToCart');
  trackServerSideFacebookEvent('AddToCart');
  trackGoogleAnalyticsEvent('profile started');
  fireTikTokPixel('AddToCart');
  sendGoogleTagManagerEvent({
    event: 'ProfileStart',
    uuid: store.get('uuid'),
  });
  sendGoogleTagManagerEvent({
    event: 'step_3_profile_start',
    uuid: store.get('uuid'),
  });
};

const trackCompleteContact = function () {
  window.LogRocket &&
    window.LogRocket.identify(store.get('uuid'), {
      name: store.get('customerName'),
      email: store.get('email'),
    });
  sendGoogleTagManagerEvent({
    event: 'CompleteContact',
    email: store.get('email'),
    hashedEmail: store.get('hashedEmail'),
    uuid: store.get('uuid'),
    customerName: store.get('customerName'),
  });
  sendGoogleTagManagerEvent({
    event: 'step_4_complete_contact',
    email: store.get('email'),
    hashedEmail: store.get('hashedEmail'),
    uuid: store.get('uuid'),
    customerName: store.get('customerName'),
  });
};

const trackAddressPage = function () {
  trackGoogleAnalyticsEvent('profile delivery address');
};

const trackCompleteAddress = function () {
  fireFacebookPixel('AddPaymentInfo');
  fireTikTokPixel('AddBilling');
  trackAdWordsConversion('AW-936326366/_UUQCMbo9GsQ3um8vgM');
  trackBingEvent('complete delivery address');
  trackPodsightsEvent('lead');
  sendGoogleTagManagerEvent({
    event: 'CompleteAddress',
    uuid: store.get('uuid'),
    customerName: store.get('customerName'),
    email: store.get('email'),
    hashedEmail: store.get('hashedEmail'),
    street: store.get('street'),
    apt: store.get('apt'),
    city: store.get('city'),
    state: store.get('state'),
    zipcode: store.get('zipcode'),
  });
  sendGoogleTagManagerEvent({
    event: 'step_5_complete_address',
    uuid: store.get('uuid'),
    customerName: store.get('customerName'),
    email: store.get('email'),
    hashedEmail: store.get('hashedEmail'),
    street: store.get('street'),
    apt: store.get('apt'),
    city: store.get('city'),
    state: store.get('state'),
    zipcode: store.get('zipcode'),
  });
};

const trackCreateAccount = function () {
  const costBeforeTaxAndPromo =
    store.get('orderCosts').subscriptionPlanSubtotal || store.get('orderCosts').subtotal;
  fireFacebookPixel('Subscribe');
  fireFacebookPixel('Purchase');
  trackServerSideFacebookEvent('Purchase');
  trackServerSideFacebookEvent('Subscribe');
  fireTikTokPixel('Purchase');
  trackFriendBuyEvent('purchase', store.get('uuid'), {
    // Purchase Events in the FriendBuy docs call for an Order ID.
    // Because we only track the first Order, we opt to use the profile ID.
    amount: getPaidTotal(),
    currency: 'USD',
    referralCode: friendBuyReferralCode(),
    isNewCustomer: true,
    customer: {
      id: store.get('uuid'),
      email: store.get('email'),
      name: store.get('customerName'),
    },
  });
  sendGoogleTagManagerEvent({
    event: 'CreateAccount',
    email: store.get('email'),
    hashedEmail: store.get('hashedEmail'),
    customerName: store.get('customerName'),
    uuid: store.get('uuid'),
    costBeforeTaxAndPromo: costBeforeTaxAndPromo,
    totalPaid: getPaidTotal(),
  });
};

/**
 * Fires account creation tracking pixels that require cookied values.
 *
 * Cookied values are deleted by the server upon account creation, so the caller
 * of this function needs to store these values before the 'create account'
 * API call is made.
 *
 * @param {String} consumerAffairsId
 */
const trackCreateAccountWithCookies = (consumerAffairsId) => {
  if (consumerAffairsId) {
    trackCAConversion(consumerAffairsId);
  }
};

export default {
  trackServerSideFacebookEvent,
  trackAddPurchasingMotivations,
  trackAddressPage,
  trackCompleteAddress,
  trackCompleteContact,
  trackCreateAccount,
  trackCreateAccountWithCookies,
  trackCreateMealPlan,
  trackProfileStart,
  trackStartBuildABowl,
};
